import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';

type Competition = Database['public']['Tables']['sync_competitions']['Row'];
type Participant = Database['public']['Tables']['sync_competition_participants']['Row'];

export function useSyncCompetition() {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCompetitions = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('sync_competitions')
        .select('*')
        .eq('is_active', true)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setCompetitions(data as Competition[] || []);
    } catch (error) {
      logger.error('Failed to fetch competitions', error);
      toast.error('Erro ao carregar competições');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchParticipants = useCallback(async (competitionId: string) => {
    try {
      const { data, error } = await supabase
        .from('sync_competition_participants')
        .select('*')
        .eq('competition_id', competitionId)
        .order('score', { ascending: false });

      if (error) throw error;
      setParticipants(data as Participant[] || []);
    } catch (error) {
      logger.error('Failed to fetch participants', error);
    }
  }, []);

  const joinCompetition = useCallback(async (competitionId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('sync_competition_participants')
        .insert({
          competition_id: competitionId,
          user_id: user.id,
        });

      if (error) throw error;

      toast.success('Você entrou na competição!');
      await fetchParticipants(competitionId);
    } catch (error) {
      logger.error('Failed to join competition', error);
      toast.error('Erro ao entrar na competição');
    }
  }, [fetchParticipants]);

  const updateScore = useCallback(async (
    competitionId: string,
    syncQuality: number,
    trackCount: number
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.functions.invoke('update-competition-score', {
        body: {
          competition_id: competitionId,
          sync_quality: syncQuality,
          track_count: trackCount,
        }
      });

      if (error) throw error;
    } catch (error) {
      logger.error('Failed to update competition score', error);
    }
  }, []);

  return {
    competitions,
    participants,
    isLoading,
    fetchCompetitions,
    fetchParticipants,
    joinCompetition,
    updateScore,
  };
}
